// Reactive project store. Holds the project data + decoded audio buffers,
// emits change events, and provides mutation helpers with basic undo/redo.
import { Emitter, uid } from "../util";
import { AudioAsset, Clip, Effect, Project, Track, newProject } from "./model";

type ChangeScope = "project" | "tracks" | "transport" | "selection" | "assets" | "mixer";

interface StoreEvents {
  change: { scope: ChangeScope };
  selection: { trackId: string | null; clipId: string | null };
}

export class Store extends Emitter<StoreEvents> {
  project: Project;
  /** assetId -> decoded buffer (kept out of undo snapshots) */
  buffers = new Map<string, AudioBuffer>();
  selectedTrackId: string | null = null;
  selectedClipId: string | null = null; // "primary" clip (last clicked)
  selectedClipIds = new Set<string>();   // full multi-selection

  private undoStack: string[] = [];
  private redoStack: string[] = [];

  constructor(project?: Project) {
    super();
    this.project = project ?? newProject();
    this.selectedTrackId = this.project.tracks[0]?.id ?? null;
  }

  emitChange(scope: ChangeScope = "project") {
    this.project.modifiedAt = Date.now();
    this.emit("change", { scope });
  }

  // ---- undo/redo -------------------------------------------------------
  private snapshot(): string {
    return JSON.stringify(this.project);
  }
  pushUndo() {
    this.undoStack.push(this.snapshot());
    if (this.undoStack.length > 100) this.undoStack.shift();
    this.redoStack.length = 0;
  }
  undo() {
    const prev = this.undoStack.pop();
    if (!prev) return;
    this.redoStack.push(this.snapshot());
    this.project = JSON.parse(prev);
    this.emitChange("project");
  }
  redo() {
    const next = this.redoStack.pop();
    if (!next) return;
    this.undoStack.push(this.snapshot());
    this.project = JSON.parse(next);
    this.emitChange("project");
  }

  /** Change the project tempo. MIDI/sequencer clips are time-scaled so the
   *  arrangement stays locked to the bar grid at the new BPM; audio clips keep
   *  their real-time position (they aren't tempo-locked unless time-stretched). */
  setTempo(tempo: number) {
    const nt = Math.max(20, Math.min(300, Math.round(tempo)));
    const old = this.project.tempo;
    if (!nt || nt === old) { this.emitChange("project"); return; }
    this.pushUndo();
    const f = old / nt; // seconds scale: faster tempo → shorter durations
    for (const track of this.project.tracks)
      for (const clip of track.clips)
        if (clip.notes) {
          clip.start *= f; clip.duration *= f; clip.offset *= f;
          for (const n of clip.notes) { n.start *= f; n.dur *= f; }
        }
    this.project.tempo = nt;
    this.emitChange("project");
  }

  // ---- selection -------------------------------------------------------
  select(trackId: string | null, clipId: string | null = null) {
    this.selectedTrackId = trackId;
    this.selectedClipId = clipId;
    this.selectedClipIds = clipId ? new Set([clipId]) : new Set();
    this.emit("selection", { trackId, clipId });
  }

  isClipSelected(id: string): boolean { return this.selectedClipIds.has(id); }

  /** Toggle a clip in the multi-selection (modifier-click). */
  toggleClipSelection(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    if (this.selectedClipIds.has(clipId)) {
      this.selectedClipIds.delete(clipId);
      if (this.selectedClipId === clipId) this.selectedClipId = [...this.selectedClipIds].pop() ?? null;
    } else {
      this.selectedClipIds.add(clipId);
      this.selectedClipId = clipId;
      this.selectedTrackId = found.track.id;
    }
    this.emit("selection", { trackId: this.selectedTrackId, clipId: this.selectedClipId });
  }

  /** Replace the multi-selection with the given clip ids (e.g. marquee). */
  setClipSelection(ids: string[]) {
    this.selectedClipIds = new Set(ids);
    this.selectedClipId = ids[ids.length - 1] ?? null;
    const f = this.selectedClipId ? this.findClip(this.selectedClipId) : null;
    if (f) this.selectedTrackId = f.track.id;
    this.emit("selection", { trackId: this.selectedTrackId, clipId: this.selectedClipId });
  }

  /** Resolve the current multi-selection to track/clip pairs. */
  selectedClipList(): { track: Track; clip: Clip }[] {
    const out: { track: Track; clip: Clip }[] = [];
    for (const t of this.project.tracks) for (const c of t.clips) if (this.selectedClipIds.has(c.id)) out.push({ track: t, clip: c });
    return out;
  }

  // ---- lookups ---------------------------------------------------------
  track(id: string): Track | undefined {
    return this.project.tracks.find((t) => t.id === id);
  }
  asset(id: string): AudioAsset | undefined {
    return this.project.assets.find((a) => a.id === id);
  }
  findClip(clipId: string): { track: Track; clip: Clip } | undefined {
    for (const track of this.project.tracks) {
      const clip = track.clips.find((c) => c.id === clipId);
      if (clip) return { track, clip };
    }
    return undefined;
  }

  // ---- assets ----------------------------------------------------------
  addAsset(buffer: AudioBuffer, name: string): AudioAsset {
    const asset: AudioAsset = {
      id: uid("ast"),
      name,
      sampleRate: buffer.sampleRate,
      channels: buffer.numberOfChannels,
      length: buffer.length,
      duration: buffer.duration,
    };
    this.buffers.set(asset.id, buffer);
    this.project.assets.push(asset);
    this.emitChange("assets");
    return asset;
  }

  // ---- mutations -------------------------------------------------------
  addTrack(track: Track) {
    this.pushUndo();
    this.project.tracks.push(track);
    this.select(track.id, null);
    this.emitChange("tracks");
  }
  removeTrack(id: string) {
    this.pushUndo();
    this.project.tracks = this.project.tracks.filter((t) => t.id !== id);
    if (this.selectedTrackId === id) this.select(this.project.tracks[0]?.id ?? null);
    this.emitChange("tracks");
  }
  /** Delete several tracks at once (one undo step) — used by the delete dialog. */
  removeTracks(ids: string[]) {
    const set = new Set(ids);
    if (!set.size) return;
    this.pushUndo();
    this.project.tracks = this.project.tracks.filter((t) => !set.has(t.id));
    if (this.selectedTrackId && set.has(this.selectedTrackId)) this.select(this.project.tracks[0]?.id ?? null);
    this.emitChange("tracks");
  }
  /** Save (or overwrite) a step-sequencer pattern in the project by id. */
  upsertSequence(seq: import("./model").SavedSequence) {
    this.pushUndo();
    const list = (this.project.sequences ??= []);
    const i = list.findIndex((s) => s.id === seq.id);
    if (i >= 0) list[i] = seq; else list.push(seq);
    this.emitChange("project");
  }
  deleteSequence(id: string) {
    if (!this.project.sequences) return;
    this.pushUndo();
    this.project.sequences = this.project.sequences.filter((s) => s.id !== id);
    this.emitChange("project");
  }
  updateTrack(id: string, patch: Partial<Track>, scope: ChangeScope = "mixer") {
    const t = this.track(id);
    if (!t) return;
    Object.assign(t, patch);
    this.emitChange(scope);
  }
  addClip(trackId: string, clip: Clip) {
    this.pushUndo();
    this.track(trackId)?.clips.push(clip);
    this.emitChange("tracks");
  }
  updateClip(clipId: string, patch: Partial<Clip>, takeUndo = true) {
    const found = this.findClip(clipId);
    if (!found) return;
    if (takeUndo) this.pushUndo();
    Object.assign(found.clip, patch);
    this.emitChange("tracks");
  }
  removeClip(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    this.pushUndo();
    found.track.clips = found.track.clips.filter((c) => c.id !== clipId);
    if (this.selectedClipId === clipId) this.selectedClipId = null;
    this.emitChange("tracks");
  }

  /** Move a clip to a different track (drag across lanes). Becomes standalone. */
  moveClipToTrack(clipId: string, targetTrackId: string) {
    const found = this.findClip(clipId);
    const target = this.track(targetTrackId);
    if (!found || !target || found.track.id === targetTrackId) return;
    found.track.clips = found.track.clips.filter((c) => c.id !== clipId);
    found.clip.takeGroupId = undefined;
    found.clip.takeActive = undefined;
    target.clips.push(found.clip);
    this.selectedTrackId = targetTrackId;
    this.emitChange("tracks");
  }

  bufferDuration(assetId: string): number {
    return this.buffers.get(assetId)?.duration ?? this.asset(assetId)?.duration ?? Infinity;
  }

  /** Split one clip at absolute timeline `time`, returning the new right-hand clip. */
  private splitOne(track: Track, clip: Clip, time: number, newGroupL?: string, newGroupR?: string): Clip | null {
    const end = clip.start + clip.duration;
    if (time <= clip.start + 0.01 || time >= end - 0.01) return null;
    const right: Clip = {
      ...clip,
      id: uid("clip"),
      start: time,
      offset: clip.offset + (time - clip.start),
      duration: end - time,
      fadeIn: 0.005,
      takeGroupId: newGroupR,
      // MIDI clips share notes across the split as trim windows; give each side
      // its own array so later edits don't leak between the halves.
      notes: clip.notes ? JSON.parse(JSON.stringify(clip.notes)) : undefined,
    };
    clip.duration = time - clip.start;
    clip.fadeOut = 0.005;
    clip.takeGroupId = newGroupL;
    track.clips.push(right);
    return right;
  }

  /** Split the clip at `time`. If it belongs to a take group, split every take in
   *  the group at that time so each side becomes its own comp slot. */
  splitClipAt(clipId: string, time: number) {
    const found = this.findClip(clipId);
    if (!found) return;
    this.pushUndo();
    const { track, clip } = found;
    if (clip.takeGroupId) {
      const groupL = uid("tg"), groupR = uid("tg");
      const members = track.clips.filter((c) => c.takeGroupId === clip.takeGroupId);
      for (const m of members) this.splitOne(track, m, time, groupL, groupR);
    } else {
      this.splitOne(track, clip, time);
    }
    this.emitChange("tracks");
  }

  duplicateClip(clipId: string) {
    const found = this.findClip(clipId);
    if (!found) return;
    this.pushUndo();
    const copy: Clip = { ...found.clip, id: uid("clip"), start: found.clip.start + found.clip.duration, takeGroupId: undefined, takeActive: undefined, notes: found.clip.notes ? JSON.parse(JSON.stringify(found.clip.notes)) : undefined };
    found.track.clips.push(copy);
    this.select(found.track.id, copy.id);
    this.emitChange("tracks");
  }

  // ---- batch (multi-selection) operations: one undo step each -----------
  private idsForBatch(ids?: string[]): string[] {
    return ids && ids.length ? ids : [...this.selectedClipIds];
  }

  removeClips(ids?: string[]) {
    const set = new Set(this.idsForBatch(ids));
    if (!set.size) return;
    this.pushUndo();
    for (const t of this.project.tracks) t.clips = t.clips.filter((c) => !set.has(c.id));
    this.selectedClipIds.clear();
    this.selectedClipId = null;
    this.emitChange("tracks");
  }

  duplicateClips(ids?: string[]) {
    const list = this.idsForBatch(ids);
    if (!list.length) return;
    this.pushUndo();
    const newIds: string[] = [];
    for (const id of list) {
      const found = this.findClip(id);
      if (!found) continue;
      const copy: Clip = { ...found.clip, id: uid("clip"), start: found.clip.start + found.clip.duration, takeGroupId: undefined, takeActive: undefined, notes: found.clip.notes ? JSON.parse(JSON.stringify(found.clip.notes)) : undefined };
      found.track.clips.push(copy);
      newIds.push(copy.id);
    }
    this.selectedClipIds = new Set(newIds);
    this.selectedClipId = newIds[newIds.length - 1] ?? this.selectedClipId;
    this.emitChange("tracks");
  }

  splitClipsAt(time: number, ids?: string[]) {
    const list = this.idsForBatch(ids);
    if (!list.length) return;
    this.pushUndo();
    for (const id of list) {
      const found = this.findClip(id);
      if (!found) continue;
      const { track, clip } = found;
      if (clip.takeGroupId) {
        const groupL = uid("tg"), groupR = uid("tg");
        for (const m of track.clips.filter((c) => c.takeGroupId === clip.takeGroupId)) this.splitOne(track, m, time, groupL, groupR);
      } else {
        this.splitOne(track, clip, time);
      }
    }
    this.emitChange("tracks");
  }

  /** Apply a patch to every selected clip (e.g. gain). One undo step. */
  updateSelectedClips(patch: Partial<Clip>, takeUndo = true) {
    const list = this.idsForBatch();
    if (!list.length) return;
    if (takeUndo) this.pushUndo();
    for (const id of list) { const f = this.findClip(id); if (f) Object.assign(f.clip, patch); }
    this.emitChange("tracks");
  }

  /** Trim left/right edge keeping audio aligned to the source. */
  trimClip(clipId: string, edge: "left" | "right", newTime: number) {
    const found = this.findClip(clipId);
    if (!found) return;
    const { clip } = found;
    const end = clip.start + clip.duration;
    if (edge === "left") {
      const minStart = clip.start - clip.offset; // offset can't go below 0
      const ns = Math.max(0, minStart, Math.min(newTime, end - 0.02));
      clip.offset += ns - clip.start;
      clip.duration = end - ns;
      clip.start = ns;
    } else {
      const maxEnd = clip.start + (this.bufferDuration(clip.assetId) - clip.offset);
      const ne = Math.min(maxEnd, Math.max(newTime, clip.start + 0.02));
      clip.duration = ne - clip.start;
    }
    this.emitChange("tracks");
  }

  /** Promote a take to active within its group (others muted). */
  setActiveTake(clipId: string) {
    const found = this.findClip(clipId);
    if (!found || !found.clip.takeGroupId) return;
    this.pushUndo();
    for (const c of found.track.clips)
      if (c.takeGroupId === found.clip.takeGroupId) c.takeActive = c.id === clipId;
    this.select(found.track.id, clipId);
    this.emitChange("tracks");
  }

  /** Clear a time range on a track, trimming/splitting any clips that overlap it
   *  (used by Overwrite record mode so the new take punches over old audio). */
  overwriteRegion(trackId: string, start: number, end: number, exceptClipId?: string) {
    const track = this.track(trackId);
    if (!track || end <= start) return;
    const result: Clip[] = [];
    for (const c of track.clips) {
      const cEnd = c.start + c.duration;
      if (c.id === exceptClipId || cEnd <= start || c.start >= end) { result.push(c); continue; }
      if (c.start < start) { // keep the part before the region
        result.push({ ...c, id: uid("clip"), duration: start - c.start, fadeOut: 0.005, takeGroupId: undefined, takeActive: undefined });
      }
      if (cEnd > end) { // keep the part after the region
        result.push({ ...c, id: uid("clip"), start: end, offset: c.offset + (end - c.start), duration: cEnd - end, fadeIn: 0.005, takeGroupId: undefined, takeActive: undefined });
      }
      // the overlapped middle is dropped
    }
    track.clips = result;
    this.emitChange("tracks");
  }

  /** Group a freshly recorded clip with any clips it overlaps into a take stack. */
  groupAsTake(trackId: string, newClipId: string) {
    const track = this.track(trackId);
    const nc = track?.clips.find((c) => c.id === newClipId);
    if (!track || !nc) return;
    const s = nc.start, e = nc.start + nc.duration;
    const overlapping = track.clips.filter((c) => c.id !== newClipId && c.start < e && c.start + c.duration > s);
    if (overlapping.length === 0) return;
    const groupId = overlapping.find((c) => c.takeGroupId)?.takeGroupId ?? uid("tg");
    for (const c of overlapping) { c.takeGroupId = groupId; c.takeActive = false; }
    // also fold in any prior members of that group not currently overlapping
    for (const c of track.clips) if (c.takeGroupId === groupId) c.takeActive = false;
    nc.takeGroupId = groupId;
    nc.takeActive = true;
    track.compExpanded = true;
    this.emitChange("tracks");
  }

  toggleCompExpand(trackId: string) {
    const t = this.track(trackId);
    if (!t) return;
    t.compExpanded = !t.compExpanded;
    this.emitChange("tracks");
  }

  /** Active takes only (used by engine/render). A clip is active unless it is an
   *  inactive member of a take group. */
  activeClips(track: Track): Clip[] {
    return track.clips.filter((c) => c.takeActive !== false);
  }

  // ---- effects ---------------------------------------------------------
  addEffect(trackId: string | "master", fx: Effect) {
    this.pushUndo();
    if (trackId === "master") this.project.masterFx.push(fx);
    else this.track(trackId)?.fx.push(fx);
    this.emitChange("mixer");
  }
  removeEffect(trackId: string | "master", fxId: string) {
    this.pushUndo();
    const list = trackId === "master" ? this.project.masterFx : this.track(trackId)?.fx;
    if (!list) return;
    const i = list.findIndex((f) => f.id === fxId);
    if (i >= 0) list.splice(i, 1);
    this.emitChange("mixer");
  }
  updateEffect(trackId: string | "master", fxId: string, patch: Partial<Effect>) {
    const list = trackId === "master" ? this.project.masterFx : this.track(trackId)?.fx;
    const fx = list?.find((f) => f.id === fxId);
    if (!fx) return;
    // Merge params (a partial {params:{key:v}} must not drop the other params).
    const { params, ...rest } = patch;
    Object.assign(fx, rest);
    if (params) Object.assign(fx.params, params);
    this.emitChange("mixer");
  }

  /** Save a user effect preset into the project (kind-scoped). */
  saveFxPreset(preset: import("./model").FxPreset) {
    this.pushUndo();
    (this.project.fxPresets ??= []).push(preset);
    this.emitChange("mixer");
  }
  deleteFxPreset(id: string) {
    if (!this.project.fxPresets) return;
    this.pushUndo();
    this.project.fxPresets = this.project.fxPresets.filter((p) => p.id !== id);
    this.emitChange("mixer");
  }

  /** Total content length in seconds (for timeline sizing). */
  contentLength(): number {
    return Math.max(16, this.contentEnd());
  }

  /** End of the last (audible) clip in seconds — 0 for an empty project. */
  contentEnd(): number {
    let max = 0;
    for (const t of this.project.tracks)
      for (const c of t.clips) if (c.takeActive !== false) max = Math.max(max, c.start + c.duration);
    return max;
  }

  /** The range the loop button repeats: the loop region if one is set,
   *  otherwise the whole arrangement up to the end of its last bar. */
  loopRange(): { start: number; end: number } | null {
    const p = this.project;
    if (p.loopEnd > p.loopStart) return { start: p.loopStart, end: p.loopEnd };
    const end = this.contentEnd();
    if (end <= 0) return null;
    const bar = (60 / p.tempo) * p.beatsPerBar;
    return { start: 0, end: Math.ceil(end / bar - 1e-6) * bar };
  }

  /** Set (or with end <= start, clear) the loop region. One undo step. */
  setLoopRegion(start: number, end: number) {
    this.pushUndo();
    const s = Math.max(0, Math.min(start, end)), e = Math.max(start, end);
    this.project.loopStart = e - s > 0.01 ? s : 0;
    this.project.loopEnd = e - s > 0.01 ? e : 0;
    this.emitChange("transport");
  }

  /** Are any tracks soloed? */
  anySolo(): boolean {
    return this.project.tracks.some((t) => t.solo);
  }
}
